import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Typography,
  Paper,
  Link,
} from '@mui/material'
import { useState } from 'react'
import { useAuth } from '@hooks/useAuth'
import normalizeUrl from 'normalize-url'
import { createConcierge } from '@lib/createConcierge'
import CreateConciergeAnimation from '@components/CreateConciergeAnimation'

export default function CreateConciergePage() {
  const { user } = useAuth()
  const [url, setUrl] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const product = 'free'

  function getSanitizedUrl(raw: string): string | null {
    try {
      const normalized = normalizeUrl(raw.trim())
      const urlObj = new URL(normalized)
      if (!/\.\w{2,}$/i.test(urlObj.hostname)) return null
      return urlObj.origin
    } catch {
      return null
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()

    const sanitizedUrl = getSanitizedUrl(url)
    if (!sanitizedUrl) {
      setError('Please enter a valid website URL.')
      return
    }

    setError('')

    try {
      setLoading(true)
      const response = await createConcierge(sanitizedUrl, user.email, product)

      if (!response.ok) {
        const data = await response.json()
        setError(data.error || 'Something went wrong. Please try again.')
        return
      }

      setSubmitted(true)
    } catch (err) {
      console.error(err)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      {submitted ? (
        <CreateConciergeAnimation />
      ) : (
        <Paper
          elevation={1}
          sx={{
            maxWidth: 480,
            width: '100%',
            p: 4,
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Typography gutterBottom variant='h2' sx={{ textAlign: 'center' }}>
            Let&apos;s create your concierge ✨
          </Typography>
          <Typography variant='subtitle1' sx={{ textAlign: 'center', mb: 3 }}>
            We couldn’t find a concierge connected to your account. No worries —
            just add your website URL to get started.
          </Typography>

          <form onSubmit={handleSubmit}>
            <FormControl fullWidth error={!!error}>
              <Typography variant='body2'>Your Website URL</Typography>
              <OutlinedInput
                type='text'
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder='yourdomain.com'
                sx={{ mb: 1 }}
              />
              {error && (
                <FormHelperText sx={{ color: 'error.main' }}>
                  {error}
                </FormHelperText>
              )}
            </FormControl>

            <Button
              type='submit'
              variant='contained'
              color='secondary'
              fullWidth
              size='large'
              sx={{
                height: '48px',
                mt: 2,
                '&.Mui-disabled': {
                  color: 'primary.contrastText',
                },
              }}
              disabled={loading}
            >
              Create Concierge
            </Button>
          </form>
          <Box mt={4} textAlign='center'>
            <Typography variant='body2' color='text.secondary'>
              Logged in as <strong>{user?.email || 'unknown'}</strong>
            </Typography>
            <Typography variant='body2' color='text.secondary' mt={1}>
              If you believe this is a mistake,{' '}
              <Link href='mailto:support@webwisdom.ai?subject=Missing Concierge Issue'>
                contact support
              </Link>
              .
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  )
}
